import { Button } from "@/components/ui/button.tsx";
import { MultilineInput } from "@/components/ui/multiline-input.tsx";
import { saveSentMessage } from "@/lib/api/messages.ts";
import { usePostHogEvent } from "@/lib/posthog";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils.ts";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import { sendbirdSelectors } from "@sendbird/uikit-react";
import { useSafeChannelContext } from "@/lib/sendbird/use-safe-channel-context";
import { VoiceMessageInputWrapper } from "@sendbird/uikit-react/GroupChannel/components/MessageInputWrapper";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { Loader2, Mic, SendIcon, Sparkles } from "lucide-react";
import { ChangeEvent, useCallback, useRef, useState } from "react";

import { AIConversation } from "@/components/conversations/ai-conversation.tsx";
import {
  AISuggestedReplies,
  CurrentAISuggestion,
} from "@/components/conversations/ai-suggested-replies.tsx";
import { NewQuotedMessage } from "@/components/conversations/quoted-message.tsx";
import { VideoRecordingDialogTrigger } from "@/components/conversations/video-recording-dialog.tsx";
import { useToast } from "@/components/ui/use-toast.ts";
import { useGetMyMentorships } from "@/lib/api/mentorships.ts";
import { useCreateNote } from "@/lib/api/notes.ts";
import { useAISuggestionFeedback } from "@/lib/hooks/ai/use-ai-suggestion-feedback";
import { useGetChannelMentorship } from "@/lib/hooks/conversations/use-get-channel-mentorship";
import { useChatOverride } from "@/lib/hooks/conversations/useChatOverride";
import { GifIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import { MessageMetaArray } from "@sendbird/chat/message";
import { createPortal } from "react-dom";
import { CallButton } from "./call-button";

import { ImageUploadWithHomescreen } from "@/components/conversations/image-upload-with-homescreen";
import { useSaveHomescreenImage } from "@/lib/api/homescreen-images";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { useOpsPersistMessage } from "@/lib/hooks/conversations/use-ops-persist-message";
import { useMessageInputStore } from "@/lib/hooks/conversations/use-message-input";
import { useFeatureFlagEnabled } from "@posthog/react";
import { GifPicker } from "@/components/conversations/gif-picker.tsx";
import { useAbility } from "@/lib/casl/ability-context";
import { getTeenLanguageFromChannel } from "@/lib/sendbird/translation";

type ClientSendbirdMessage = Parameters<typeof saveSentMessage>[0];

export interface ResolvedOpsChannel {
  groupChannel: GroupChannel;
  internalChannelId: string;
}

export interface MessageInputOpsContext {
  internalChannelId: string | null;
  onAfterSend?: () => void;
  /**
   * Lazy channel resolver for new-chat compose. When provided, the input
   * defers channel creation to the first send/voice click — it awaits
   * `ensureChannel()`, caches the result, and uses it for both the Sendbird
   * send and the ops persistence call.
   */
  ensureChannel?: () => Promise<ResolvedOpsChannel>;
}

interface MessageInputProps {
  shouldShowAiConversation?: boolean;
  shouldShowVideoCallButton?: boolean;
  isAdminView?: boolean;
  isSupervisorView?: boolean;
  opsContext?: MessageInputOpsContext;
}

export function MessageInput({
  shouldShowAiConversation = true,
  shouldShowVideoCallButton = false,
  isAdminView,
  isSupervisorView,
  opsContext,
}: MessageInputProps) {
  const ability = useAbility();
  const canCreateMessages = ability.can("create", "messages");
  const canCreateCalls = ability.can("create", "calls");
  const [showVoiceMessageInput, setShowVoiceMessageInput] = useState(false);
  const [isSendingVoice, setIsSendingVoice] = useState(false);
  const [overriddenMessageInput, setOverriddenMessageInput] = useState("");
  const [showAIConversation, setShowAIConversation] = useState(false);
  const [showAISuggestedReplies, setShowAISuggestedReplies] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);

  const { mentorship } = useGetChannelMentorship();

  const { isOverridden, overriddenBy } = useChatOverride(mentorship?.id);

  const allowHomeScreenImageUpload = useFeatureFlagEnabled("ios-widget");
  const isGifEnabled = useFeatureFlagEnabled("enable-gifs");

  const {
    messageInputs,
    setMessageInput,
    setSupervisorMessageInput,
    supervisorMessageInput,
  } = useMessageInputStore();
  const { currentConversation } = useCurrentConversation();

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const store = useSendbirdStateContext();
  const userId = store.config.userId;

  const channelStore = useSafeChannelContext();
  const channel = channelStore?.currentGroupChannel;

  const draftKey =
    currentConversation ??
    channel?.url ??
    (opsContext?.ensureChannel ? "__new_ops_chat__" : null);

  const currentInput = isAdminView
    ? overriddenMessageInput
    : isSupervisorView
      ? supervisorMessageInput
      : draftKey
        ? messageInputs[draftKey] || ""
        : "";

  const {
    userClickedSuggestion,
    userSentMessage,
    userDiscardedVisibleSuggestion,
    userRequestedNewApproach,
    userIgnoredAISuggestion,
  } = useAISuggestionFeedback({
    mentorshipId: mentorship?.id,
    userId: userId,
  });

  const sendUserMessageInternal = sendbirdSelectors.getSendUserMessage(store);
  const sendFileMessage = sendbirdSelectors.getSendFileMessage(store);

  const { toast } = useToast();

  const quotedMessage = channelStore?.quoteMessage;

  const membersExcludingMe = channel?.members?.filter(
    (member) => member.userId !== store.config.userId,
  );

  const toUserId = membersExcludingMe?.[0]?.userId;

  const [forceUpdate, setForceUpdate] = useState(0);
  const { data: mentorships } = useGetMyMentorships();

  const mentorshipWithMentee = mentorships?.find(
    (mentorship) => mentorship.mentee?.id === toUserId,
  );

  const { mutateAsync: createNote } = useCreateNote();

  const { dispatchEvent: trackAiEvent } = usePostHogEvent("ai_suggestion");
  const { track } = useTrackEvent();

  const [currentAISuggestion, setCurrentAISuggestion] =
    useState<CurrentAISuggestion | null>(null);

  const { mutateAsync: saveHomescreenImage } = useSaveHomescreenImage();

  const persistOpsMessage = useOpsPersistMessage(opsContext?.onAfterSend);

  const [resolvedDeferred, setResolvedDeferred] =
    useState<ResolvedOpsChannel | null>(null);

  const effectiveChannel = channel ?? resolvedDeferred?.groupChannel ?? null;

  const resolveChannel = useCallback(async (): Promise<{
    groupChannel: GroupChannel;
    internalChannelId: string | null;
  }> => {
    if (channel) {
      return {
        groupChannel: channel,
        internalChannelId: opsContext?.internalChannelId ?? null,
      };
    }
    if (resolvedDeferred) return resolvedDeferred;
    if (opsContext?.ensureChannel) {
      const r = await opsContext.ensureChannel();
      setResolvedDeferred(r);
      return r;
    }
    throw new Error("MessageInput: no channel available to send into");
  }, [channel, opsContext, resolvedDeferred]);

  const persistMessage = (
    sentMessage: ClientSendbirdMessage,
    file: File | null,
    internalChannelId: string | null,
  ) => {
    if (opsContext) {
      if (!internalChannelId) return;
      return persistOpsMessage({ internalChannelId, sentMessage, file });
    }
    return saveSentMessage(sentMessage, file, toUserId!);
  };

  if (!canCreateMessages) return null;

  const handleSendMessage = async () => {
    const hasTextMessage = currentInput.trim().length > 0;
    const hasGifMessage = selectedGif !== null;

    if (!hasTextMessage && !hasGifMessage) return;

    const suggestionUsed = userSentMessage(currentInput);
    if (
      !suggestionUsed &&
      showAISuggestedReplies &&
      currentInput.trim().length > 0 &&
      currentAISuggestion &&
      ((currentAISuggestion.type === "default" &&
        currentInput !== currentAISuggestion.statement &&
        currentInput !== currentAISuggestion.question) ||
        ((currentAISuggestion.type === "specific" ||
          currentAISuggestion.type === "priority") &&
          currentInput !== currentAISuggestion.draft))
    ) {
      userIgnoredAISuggestion({
        suggestion: currentAISuggestion,
        sentMessage: currentInput,
      });
    }

    // Capture values before clearing
    const textToSend = currentInput.trim();
    const gifToSend = selectedGif;
    const replyToMessage = quotedMessage;

    // Clear input immediately for instant feedback
    if (hasTextMessage) {
      if (isAdminView) {
        setOverriddenMessageInput("");
      } else if (isSupervisorView) {
        setSupervisorMessageInput("");
      } else if (draftKey) {
        setMessageInput(draftKey, "");
      }
    }
    if (hasGifMessage) setSelectedGif(null);
    setForceUpdate((prev) => prev + 1);

    try {
      const isOpsMode = !!opsContext;
      const isNewOpsChannelSend =
        isOpsMode && !channel && !!opsContext?.ensureChannel;

      const { groupChannel, internalChannelId } = await resolveChannel();

      // Send GIF first as user message with customType
      if (hasGifMessage && gifToSend) {
        const gifParams = {
          message: gifToSend,
          customType: "GIF_MESSAGE",
          ...(replyToMessage && {
            parentMessageId: replyToMessage.messageId,
          }),
        };

        await sendUserMessageInternal(groupChannel, gifParams)
          .onSucceeded((message) => {
            persistMessage(message, null, internalChannelId);
          })
          .onFailed((error) => {
            console.error("GIF message sending failed", error);
          });
      }

      // Send text message (only if GIF wasn't sent, or send as separate message)
      if (hasTextMessage && textToSend) {
        const teenLang = getTeenLanguageFromChannel(groupChannel, userId);

        const messageParams = {
          message: textToSend,
          ...(replyToMessage &&
            !hasGifMessage && {
              parentMessageId: replyToMessage.messageId,
            }),
          ...(teenLang && {
            translationTargetLanguages: [teenLang],
          }),
          ...(isOverridden &&
            isAdminView && {
              customType: "SUPERVISION",
              metaArrays: [
                new MessageMetaArray({
                  key: "is_supervisor",
                  value: ["true"],
                }),
                new MessageMetaArray({
                  key: "overridden_by",
                  value: [overriddenBy!],
                }),
              ],
            }),
        };

        await sendUserMessageInternal(groupChannel, messageParams)
          .onSucceeded((message) => {
            console.log("message ==> ", message);
            persistMessage(message, null, internalChannelId);

            const suggestionUsedForThis = currentAISuggestion;
            if (suggestionUsedForThis) {
              const originalText =
                suggestionUsedForThis.type === "default"
                  ? suggestionUsedForThis.statement
                  : suggestionUsedForThis.draft;

              if (textToSend === originalText) {
                track(PostHogEvents.SUGGESTED_MESSAGE_SENT, {
                  suggestion_type: suggestionUsedForThis.type,
                  text_length: textToSend.length,
                });
              } else {
                track(PostHogEvents.SUGGESTED_MESSAGE_EDITED, {
                  suggestion_type: suggestionUsedForThis.type,
                  original_length: originalText.length,
                  final_length: textToSend.length,
                });
              }
            }

            if (isOpsMode) {
              const customType = groupChannel.customType ?? "";
              const recipientCount = Math.max(
                0,
                (groupChannel.memberCount ?? 1) - 1,
              );
              track(PostHogEvents.OPS_CHAT_MESSAGE_SEND, {
                thread_id: groupChannel.url,
                thread_type:
                  customType === "INTERNAL_COHORT"
                    ? "cohort"
                    : recipientCount > 1
                      ? "group"
                      : "user",
                recipient_count: recipientCount,
                is_cohort: customType === "INTERNAL_COHORT",
                is_first_message: isNewOpsChannelSend,
              });
            } else {
              track(PostHogEvents.MENTOR_MESSAGE_SENT, {
                teen_id: toUserId,
                mentorship_id: mentorship?.id,
                message_type: "text",
                has_quote: !!replyToMessage,
              });
            }
          })
          .onFailed((error) => {
            console.error("Text message sending failed", error);
          });
      }

      // Clear quote message after sending
      channelStore?.setQuoteMessage?.(null);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleSendFileMessage = async (
    file: File,
    onSucceeded: () => void,
    onFailed: () => void,
    sendToHomescreen: boolean = false,
  ) => {
    const isImage = file.type.startsWith("image/");
    const isAudio = file.type.startsWith("audio/");
    const isVideo = file.type.startsWith("video/");
    const shouldSaveToHomescreen = isImage && sendToHomescreen;

    const customType = isAudio
      ? "VOICE_MESSAGE"
      : isImage
        ? "IMAGE_MESSAGE"
        : isVideo
          ? "VIDEO_MESSAGE"
          : "MEDIA";

    let resolved: {
      groupChannel: GroupChannel;
      internalChannelId: string | null;
    };
    try {
      resolved = await resolveChannel();
    } catch (err) {
      console.error(err);
      onFailed();
      return;
    }
    const { groupChannel, internalChannelId } = resolved;

    sendFileMessage(groupChannel, {
      file: file,
      parentMessageId: quotedMessage?.messageId,
      customType: customType,
    })
      .onSucceeded(async (message) => {
        channelStore?.setQuoteMessage?.(null);
        await persistMessage(message, file, internalChannelId);

        if (opsContext) {
          const channelCustomType = groupChannel.customType ?? "";
          const recipientCount = Math.max(
            0,
            (groupChannel.memberCount ?? 1) - 1,
          );
          track(PostHogEvents.OPS_CHAT_MESSAGE_SEND, {
            thread_id: groupChannel.url,
            thread_type:
              channelCustomType === "INTERNAL_COHORT"
                ? "cohort"
                : recipientCount > 1
                  ? "group"
                  : "user",
            recipient_count: recipientCount,
            is_cohort: channelCustomType === "INTERNAL_COHORT",
            is_first_message: false,
            message_type: customType.toLowerCase(),
          });
        } else {
          track(PostHogEvents.MENTOR_MESSAGE_SENT, {
            teen_id: toUserId,
            mentorship_id: mentorship?.id,
            message_type: customType.toLowerCase(),
          });
        }

        // If this is an image and sendToHomescreen is checked, save to homescreen
        if (shouldSaveToHomescreen && toUserId) {
          try {
            await saveHomescreenImage({
              messageId: message.messageId.toString(),
              teenId: toUserId,
            });
            toast({
              title: "Image sent to home screen",
              description: "The image will appear on the teen's home screen",
            });
          } catch (error) {
            console.error("Failed to save homescreen image", error);
            toast({
              title: "Failed to send to home screen",
              description:
                "The message was sent, but couldn't be added to home screen",
              variant: "destructive",
            });
          }
        }

        onSucceeded();
      })
      .onFailed((error) => {
        console.error(error.stack);
        onFailed();
      });
  };

  const handleSendAttachmentMessage = async (file: File) => {
    if (file.size > 256 * 1000 * 1000) {
      toast({
        title: "File size should be less than 256MB.",
        variant: "destructive",
      });
      return;
    }

    handleSendFileMessage(
      file,
      () => {},
      () => {},
    );
  };

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handlerInputTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    clearTimeout(timerRef.current!);

    if (isAdminView) {
      setOverriddenMessageInput(event.target.value);
    } else if (isSupervisorView) {
      setSupervisorMessageInput(event.target.value);
    } else if (draftKey) {
      setMessageInput(draftKey, event.target.value);
    }

    channel?.startTyping();

    timerRef.current = setTimeout(() => {
      channel?.endTyping();
    }, 1000);
  };

  // Disabled auto-show effect for AI suggestions - mentors must manually click the light bulb
  // useEffect(() => {
  //   if (isLastMessageFromMentee && lastMessage?.messageId) {
  //     if (lastMessage.messageId !== manuallyClosedForMessageId) {
  //       setShowAISuggestedReplies(true);

  //       // Scroll to the bottom of the channel
  //       setTimeout(() => {
  //         const messageListContainer = document.querySelector(
  //           '[data-testid="sendbird-message-list-container"]',
  //         );
  //         if (messageListContainer) {
  //           messageListContainer.scrollTo({
  //             top: messageListContainer.scrollHeight + 200,
  //             behavior: "smooth",
  //           });
  //         }
  //       }, 100);
  //     } else if (!isLastMessageFromMentee) {
  //       setShowAISuggestedReplies(false);
  //       setManuallyClosedForMessageId(null);
  //     }
  //   } else if (!isLastMessageFromMentee) {
  //     setShowAISuggestedReplies(false);
  //     setManuallyClosedForMessageId(null);
  //   }
  // }, [
  //   isLastMessageFromMentee,
  //   lastMessage?.messageId,
  //   manuallyClosedForMessageId,
  // ]);

  const handleToggleAISuggestions = () => {
    const newShowState = !showAISuggestedReplies;
    setShowAISuggestedReplies(newShowState);
    trackAiEvent(undefined, {
      action: newShowState ? "show" : "hide",
      triggered_by: "button_click",
    });
  };

  return (
    <>
      {shouldShowAiConversation &&
        showAIConversation &&
        !!mentorshipWithMentee &&
        createPortal(
          <AIConversation
            onClose={() => setShowAIConversation(false)}
            mentorshipId={mentorshipWithMentee.id}
            addToInput={(text) => {
              if (currentConversation) {
                setMessageInput(currentConversation, `${currentInput} ${text}`);
              }
              inputRef.current?.focus();
            }}
            createNote={(text) => {
              createNote({
                title: new Date().toLocaleString(),
                content: text,
                mentorshipId: mentorshipWithMentee!.id,
              });
              toast({
                title: "Note created",
                description: "Note has been created successfully",
                variant: "default",
              });
            }}
          />,
          document.body,
        )}
      <div className="border-t">
        {showAISuggestedReplies && (
          <div className="p-4">
            <AISuggestedReplies
              key={forceUpdate}
              mentorship={mentorshipWithMentee!}
              onUiSuggestionClick={(text, approach?: string) => {
                if (currentConversation) {
                  setMessageInput(
                    currentConversation,
                    `${currentInput} ${text}`,
                  );
                }
                inputRef.current?.focus();
                trackAiEvent("ai_suggested_reply_selected", {
                  text_length: text.length,
                  approach: approach || "default",
                  has_quote: !!quotedMessage,
                });
                track(PostHogEvents.SUGGESTED_MESSAGE_INSERTED, {
                  suggestion_type: approach || "default",
                  text_length: text.length,
                  has_quote: !!quotedMessage,
                });
              }}
              onFeedbackSuggestionClick={userClickedSuggestion}
              onFeedbackDiscardVisibleSuggestion={
                userDiscardedVisibleSuggestion
              }
              onFeedbackRequestNewApproach={userRequestedNewApproach}
              onCurrentSuggestionChange={setCurrentAISuggestion}
              quotedMessage={quotedMessage}
              handleRemoveQuote={() => {
                channelStore?.setQuoteMessage?.(null);
              }}
            />
          </div>
        )}
        {!showAISuggestedReplies && quotedMessage && (
          <div className="p-4">
            <NewQuotedMessage
              message={quotedMessage}
              handleRemoveQuote={() => {
                channelStore?.setQuoteMessage?.(null);
              }}
            />
          </div>
        )}
        {selectedGif && (
          <div className="px-4 pt-2">
            <div className="relative inline-block">
              <img
                src={selectedGif}
                alt="Selected GIF"
                className="max-h-[150px] max-w-[200px] rounded-lg object-cover"
              />
              <button
                onClick={() => setSelectedGif(null)}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                title="Remove GIF"
              >
                ×
              </button>
            </div>
          </div>
        )}
        <div className={cn("flex flex-row items-center gap-4 px-4 pb-2 pt-3")}>
          {shouldShowAiConversation && (
            <button
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#f1f1f1] p-3 hover:bg-[#e1e1e1] active:bg-[#e1e1e1] disabled:cursor-not-allowed disabled:bg-[#f1f1f1]"
              onClick={() => {
                setShowAIConversation(!showAIConversation);
              }}
              disabled={!mentorshipWithMentee}
              title="Toggle AI Conversation"
            >
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="h-6 w-6" />
              </div>
            </button>
          )}
          {shouldShowAiConversation && (
            <button
              className={cn(
                "inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-transparent bg-[#f1f1f1] p-3 hover:bg-[#e1e1e1] active:bg-[#e1e1e1] disabled:cursor-not-allowed disabled:bg-[#f1f1f1]",
                showAISuggestedReplies && "border-gray-300",
              )}
              onClick={handleToggleAISuggestions}
              disabled={!mentorshipWithMentee}
              title="Toggle AI Suggestions"
            >
              <div className="flex items-center justify-center gap-2">
                <LightBulbIcon className="h-6 w-6" />
              </div>
            </button>
          )}
          {shouldShowVideoCallButton && canCreateCalls && (
            <CallButton otherUserId={toUserId!} isSupervisorChannel />
          )}
          {!showVoiceMessageInput ? (
            <>
              <MultilineInput
                ref={inputRef}
                value={currentInput}
                onChange={handlerInputTextChange}
                placeholder="Start typing here..."
                className="flex-1 rounded-sm"
                endIcon={
                  !currentInput &&
                  !selectedGif && (
                    <div className="mr-1 flex flex-row items-center gap-2">
                      <button
                        onClick={async () => {
                          try {
                            await resolveChannel();
                            setShowVoiceMessageInput(true);
                          } catch (err) {
                            console.error(err);
                            toast({
                              title: "Could not start voice message",
                              variant: "destructive",
                            });
                          }
                        }}
                        title="Record Voice Message"
                      >
                        <Mic className="text-muted-foreground" />
                      </button>
                      {isGifEnabled && (
                        <button
                          onClick={() => {
                            setShowGifPicker(true);
                          }}
                          title="Send GIF"
                        >
                          <GifIcon className="h-6 w-6 text-muted-foreground" />
                        </button>
                      )}
                      {allowHomeScreenImageUpload && (
                        <ImageUploadWithHomescreen
                          onImageSelect={(file, sendToHomescreen) => {
                            handleSendFileMessage(
                              file,
                              () => {},
                              () => {},
                              sendToHomescreen,
                            );
                          }}
                        />
                      )}
                      <VideoRecordingDialogTrigger
                        handleSendFile={handleSendAttachmentMessage}
                      />
                    </div>
                  )
                }
                onKeyDown={(event) => {
                  // Ctrl + Enter to send message
                  if (event.key === "Enter" && event.ctrlKey) {
                    handleSendMessage();
                  }
                  // CMD + Enter to send message
                  else if (event.key === "Enter" && event.metaKey) {
                    handleSendMessage();
                  }
                }}
              />

              <Button
                className="h-12 w-12 rounded-xl bg-black"
                onClick={handleSendMessage}
              >
                <SendIcon size={32} />
              </Button>
            </>
          ) : (
            <div className="& > relative w-full">
              {isSendingVoice && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80">
                  <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
                  <span className="ml-2 text-sm text-zinc-500">Sending...</span>
                </div>
              )}
              <VoiceMessageInputWrapper
                channel={effectiveChannel!}
                onCancelClick={() => setShowVoiceMessageInput(false)}
                onSubmitClick={(file) => {
                  setIsSendingVoice(true);
                  handleSendFileMessage(
                    file,
                    () => {
                      setIsSendingVoice(false);
                      setShowVoiceMessageInput(false);
                    },
                    () => {
                      setIsSendingVoice(false);
                      console.error("Voice message sending failed");
                    },
                  );
                }}
              />
            </div>
          )}
        </div>
        {isOverridden && (
          <div className="text-center text-xs font-normal leading-none text-pink-400">
            {!isAdminView
              ? "Your supervisor is writing a message on your behalf. Please wait."
              : "Messaging on behalf of Mentor"}
          </div>
        )}
      </div>
      <GifPicker
        isOpen={showGifPicker}
        onClose={() => setShowGifPicker(false)}
        onGifSelect={(gifUrl) => {
          setSelectedGif(gifUrl);
          setShowGifPicker(false);
        }}
      />
    </>
  );
}
